import React, { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Check, Phone, ArrowRight } from "lucide-react";
import MarketingShell from "../components/MarketingShell";
import { useAuth } from "../lib/auth";
import { SERVICE_CONTENT } from "./serviceContent";

// iter-140 — one template that renders any of the 4 service pages
// keyed by `:slug` route param. Content lives in serviceContent.js
// so the template stays layout-only.

export default function ServicePage() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    useEffect(() => { /* iter-166 — auto-redirect removed; user can browse freely */ }, [user, navigate]);

    const data = SERVICE_CONTENT[slug];
    if (!data) {
        return (
            <MarketingShell testid="service-page-not-found">
                <div className="max-w-3xl mx-auto px-4 py-24 text-center">
                    <h1 className="text-3xl font-bold text-slate-900">Service not found</h1>
                    <p className="mt-3 text-slate-600">
                        We couldn't find that page. Try one of our services below.
                    </p>
                    <div className="mt-6 flex flex-wrap justify-center gap-2">
                        {Object.values(SERVICE_CONTENT).map((s) => (
                            <Link
                                key={s.slug}
                                to={`/services/${s.slug}`}
                                className="px-4 py-2 rounded-md bg-[#007AFF] text-white text-sm font-bold hover:bg-[#0369A1]"
                            >
                                {s.title}
                            </Link>
                        ))}
                    </div>
                </div>
            </MarketingShell>
        );
    }

    return (
        <MarketingShell testid={`service-page-${data.slug}`}>
            {/* HERO */}
            <section
                className="hero-dark relative h-[52vh] min-h-[380px] flex items-end text-white"
                data-testid="service-hero"
            >
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${data.hero.img})` }}
                    aria-hidden="true"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-slate-900/30 via-slate-900/50 to-slate-900/85" aria-hidden="true" />
                <div className="relative z-10 max-w-6xl mx-auto w-full px-4 sm:px-8 pb-10">
                    <p className="text-xs uppercase tracking-[0.3em] font-bold text-[#00A3FF]">{data.hero.eyebrow}</p>
                    <h1 className="mt-2 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight uppercase">
                        {data.hero.heading}
                    </h1>
                </div>
            </section>

            {/* INTRO */}
            <section className="bg-white py-16">
                <div className="max-w-4xl mx-auto px-4 sm:px-6">
                    <p className="text-[11px] uppercase tracking-[0.25em] text-slate-500 font-bold">{data.intro.eyebrow}</p>
                    <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-slate-900 uppercase">{data.intro.heading}</h2>
                    <p className="mt-6 text-slate-700 leading-relaxed">{data.intro.body}</p>
                </div>
            </section>

            {/* PHOTO BAND 1 */}
            <PhotoBand src={data.photo} alt={`${data.title} — Steeltooth Contracting`} />

            {/* SUB-SECTION (Septic only) */}
            {data.subSection && (
                <section className="hero-dark bg-slate-900 text-white py-14">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6">
                        <p className="text-[11px] uppercase tracking-[0.25em] text-[#00A3FF] font-bold">{data.subSection.eyebrow}</p>
                        <h2 className="mt-2 text-2xl sm:text-3xl font-bold uppercase">{data.subSection.heading}</h2>
                        <p className="mt-5 text-slate-200 leading-relaxed">{data.subSection.body}</p>
                        <Link
                            to="/contact"
                            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#007AFF] hover:bg-[#0369A1] font-bold text-sm uppercase tracking-wider"
                        >
                            Contact us
                        </Link>
                    </div>
                </section>
            )}

            {/* QUALITY (dark) */}
            <section className="hero-dark bg-[#0A1128] text-slate-100 py-14">
                <div className="max-w-4xl mx-auto px-4 sm:px-6">
                    <p className="text-[11px] uppercase tracking-[0.25em] text-[#00A3FF] font-bold">{data.quality.eyebrow}</p>
                    <h2 className="mt-2 text-2xl sm:text-3xl font-bold uppercase text-white">{data.quality.heading}</h2>
                    <p className="mt-5 text-slate-300 leading-relaxed">{data.quality.body}</p>
                    <Link
                        to="/contact"
                        data-testid="service-quality-cta"
                        className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#007AFF] hover:bg-[#0369A1] text-white font-bold text-sm uppercase tracking-wider"
                    >
                        Contact us
                    </Link>
                </div>
            </section>

            {/* OFFERINGS CARD */}
            <section className="hero-dark bg-black text-slate-200 py-16">
                <div className="max-w-4xl mx-auto px-4 sm:px-6">
                    <div className="rounded-md border border-slate-700 p-6 sm:p-8">
                        <p className="text-[11px] uppercase tracking-[0.25em] text-slate-400 font-bold">{data.offerings.eyebrow}</p>
                        <h2 className="mt-2 text-2xl sm:text-3xl font-bold text-white">{data.offerings.heading}</h2>
                        {data.offerings.lede && (
                            <p className="mt-4 text-slate-300 leading-relaxed">{data.offerings.lede}</p>
                        )}
                        <ul className="mt-5 space-y-2.5 text-sm">
                            {data.offerings.items.map((item) => (
                                <li key={item} className="flex items-start gap-3">
                                    <Check className="w-4 h-4 mt-0.5 text-[#00A3FF] shrink-0" />
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* PHOTO BAND 2 */}
            <PhotoBand src={data.closingPhoto} alt={`${data.title} project`} height="h-72" />

            {/* CTA */}
            <section className="hero-dark bg-[#0A1128] text-white py-14">
                <div className="max-w-4xl mx-auto px-4 sm:px-6">
                    <h2 className="text-3xl sm:text-4xl font-bold uppercase tracking-tight">{data.cta.heading}</h2>
                    <p className="mt-5 text-slate-300 leading-relaxed">{data.cta.body}</p>
                    <div className="mt-6 flex flex-wrap gap-3">
                        <Link
                            to="/contact"
                            data-testid="service-cta-contact"
                            className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[#007AFF] hover:bg-[#0369A1] font-bold uppercase tracking-wider text-sm"
                        >
                            Contact us <ArrowRight className="w-4 h-4" />
                        </Link>
                        <a
                            href="tel:7053334376"
                            className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-slate-500 text-white font-bold uppercase tracking-wider text-sm hover:bg-white/10"
                        >
                            <Phone className="w-4 h-4" /> 705-333-4376
                        </a>
                    </div>
                </div>
            </section>
        </MarketingShell>
    );
}

function PhotoBand({ src, alt, height = "h-96" }) {
    return (
        <div className={`w-full ${height} bg-slate-800 overflow-hidden`}>
            <img
                src={src}
                alt={alt}
                className="w-full h-full object-cover"
                loading="lazy"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
        </div>
    );
}
